import { AsyncPipe, NgClass, NgStyle } from '@angular/common'
import { AfterViewInit, Component, computed, inject, ViewChild } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'

import { MenuItem } from 'primeng/api'
import { Menubar } from 'primeng/menubar'
import { Ripple } from 'primeng/ripple'
import { combineLatest, map, Observable, takeUntil } from 'rxjs'

import { Action, LanguageCode, Resource } from '@ti/contracts'
import { Profile } from '@ti/core/auth/services'
import { TspProfile } from '@ti/core/auth/services/tsp.profile'
import { Memoize } from '@ti/core/common/decorators'
import { AccessControl, DeviceService } from '@ti/core/common/services'
import { injectDestroy$ } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { AiChatOutlineIconComponent } from '@ti/core/icons/ai-support'
import { BillingIconComponent } from '@ti/core/icons/billing'
import { ChatsIconComponent } from '@ti/core/icons/chats'
import { ChevronIconComponent } from '@ti/core/icons/chevron'
import { DevIconComponent } from '@ti/core/icons/dev'
import { DeviceIconComponent } from '@ti/core/icons/device'
import { GarageIconComponent } from '@ti/core/icons/garage'
import { GeneralIconComponent } from '@ti/core/icons/general'
import { UsersIconComponent } from '@ti/core/icons/users'
import { WhiteLabelIconComponent } from '@ti/core/icons/white-label-icon'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { LangSwitchDialogComponent } from '@ti/core/ui-kit/i18n/components'
import { AvatarComponent } from '@ti/core/ui-kit/layout/components/avatar'
import { DynamicHostComponent } from '@ti/core/ui-kit/layout/components/dynamic-host'
import { FeedbackButtonComponent } from '@ti/core/ui-kit/layout/components/feedback-button'
import { TooltipDirective } from '@ti/core/ui-kit/layout/directives'
import {
  BaseUserSettingsModel,
  PersonalMenuComponent,
  UserSettingsDialogComponent,
} from '@ti/shared/user'

import { TspUserSettingsModel } from '../../../../users/models/user-settings.model'

// const XEVOLVEX_TSP_ID = 2465879

@Component({
  selector: 'app-tsp-admin-menu',
  templateUrl: 'menu.component.html',
  styleUrls: ['menu.component.scss'],
  providers: [{ provide: BaseUserSettingsModel, useClass: TspUserSettingsModel }],
  imports: [
    AsyncPipe,
    AvatarComponent,
    ChevronIconComponent,
    DynamicHostComponent,
    FeedbackButtonComponent,
    LangSwitchDialogComponent,
    Menubar,
    NgClass,
    NgStyle,
    PersonalMenuComponent,
    Ripple,
    RouterLink,
    RouterLinkActive,
    TooltipDirective,
    UserSettingsDialogComponent,
  ],
})
export class TspAdminMenuComponent implements AfterViewInit {
  protected readonly accessControl = inject(AccessControl)
  protected readonly config = inject(CONFIG)
  protected readonly router = inject(Router)
  protected readonly profile = inject(Profile)
  protected readonly tspProfile = inject(TspProfile)
  protected readonly languageService = inject(LanguageService)
  protected readonly deviceService = inject(DeviceService)
  protected readonly destroy$ = injectDestroy$()

  protected readonly TYPES = { LanguageCode }

  protected readonly MIN_SAFE_WIDTH: Record<LanguageCode, number> = {
    [LanguageCode.English]: 1620,
    [LanguageCode.Spanish]: 1790,
    [LanguageCode.Portuguese]: 1790,
    [LanguageCode.Nederlands]: 1790,
  }
  protected readonly MOBILE_MENU_THRESHOLD = 1020

  protected deviceWidth = toSignal(this.deviceService.deviceWidth$)

  protected readonly isBannerEnabled = true

  @ViewChild('menubarRef')
  protected readonly menubar!: Menubar

  @Memoize()
  protected get menuItems$(): Observable<MenuItem[]> {
    return combineLatest([
      this.accessControl.massCheck$([
        [Resource.Fleet, Action.View],
        [Resource.Device, Action.View],
        [Resource.Billing, Action.View],
        [Resource.User, Action.View],
        [Resource.WhiteLabel, Action.View],
        [Resource.AuditLog, Action.View],
        [Resource.DeveloperApi, Action.View],
        [Resource.SMS, Action.View],
      ]),
      this.languageService.massTranslate$({
        fleetAccounts: 'tsp-admin.menu.fleet-accounts',
        devices: 'tsp-admin.menu.devices',
        billing: 'tsp-admin.menu.billing',
        users: 'tsp-admin.menu.users',
        whiteLabel: 'tsp-admin.menu.white-label',
        developerAPI: 'tsp-admin.menu.developer-api',
        sms: 'tsp-admin.menu.sms',
        recyclingBin: 'tsp-admin.menu.recycling-bin',
        auditLogs: 'tsp-admin.menu.audit-logs',
        aiSupport: 'support-chat.ai-support',
      }),
      this.tspProfile.tsp$,
    ]).pipe(
      takeUntil(this.destroy$),
      map(
        ([
          [
            fleetAllowed,
            deviceAllowed,
            billingAllowed,
            userAllowed,
            whiteLabelAllowed,
            auditLogAllowed,
            developerAPIAllowed,
            smsAllowed,
          ],
          labels,
          tsp,
        ]) => {
          const options: MenuItem[] = [
            {
              label: labels.fleetAccounts,
              iconType: GarageIconComponent,
              routerLink: '/accounts',
              styleClass: fleetAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.devices,
              iconType: DeviceIconComponent,
              routerLink: '/devices',
              styleClass: deviceAllowed ? '' : 'not-allowed',
            },
          ]

          if (tsp.billingStartDate) {
            options.push({
              label: labels.billing,
              iconType: BillingIconComponent,
              routerLink: '/billing',
              styleClass: billingAllowed ? '' : 'not-allowed',
            })
          }

          options.push(
            {
              label: labels.users,
              iconType: UsersIconComponent,
              routerLink: 'users',
              styleClass: userAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.whiteLabel,
              iconType: WhiteLabelIconComponent,
              routerLink: '/white-label',
              styleClass: whiteLabelAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.sms,
              iconType: ChatsIconComponent,
              routerLink: '/sms',
              styleClass: smsAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.auditLogs,
              iconType: GeneralIconComponent,
              routerLink: '/logs',
              styleClass: auditLogAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.developerAPI,
              iconType: DevIconComponent,
              routerLink: '/developer-api',
              styleClass: developerAPIAllowed ? '' : 'not-allowed',
            },
            /* Temporarily removed from the application
            {
              label: labels.recyclingBin,
              iconType: RecycleBinIconComponent,
              routerLink: '/recycling-bin',
            },
            */
            {
              label: labels.aiSupport,
              iconType: AiChatOutlineIconComponent,
              routerLink: '/ai-support',
            },
          )

          // if (tsp.id === XEVOLVEX_TSP_ID) {
          //   options.push({
          //     label: 'AI alert verification',
          //     iconType: AlertsIconComponent,
          //     routerLink: '/alert-verification',
          //     styleClass: fleetAllowed ? '' : 'not-allowed',
          //   })
          // }

          return options
        },
      ),
    )
  }

  protected readonly showOptionLabels = computed(() => {
    const width = this.deviceWidth()
    const lang = this.languageService.current()

    if (width === undefined) return true
    if (width <= this.MOBILE_MENU_THRESHOLD) return true

    const minSafeWidth = this.MIN_SAFE_WIDTH[lang ?? LanguageCode.Spanish] // Widest threshold as fallback

    return width > minSafeWidth
  })

  public ngAfterViewInit() {
    // Disable the focus handler as a hotfix
    this.menubar.onMenuFocus = () => undefined
  }

  get bannerUrl() {
    if (this.isBannerEnabled) {
      switch (this.languageService.current()) {
        case 'en': {
          return 'url(./assets/images/banner-small-VelocityIoT-en.png)'
        }
        case 'es': {
          return 'url(./assets/images/banner-small-M2M-updated-es.png)'
        }
      }
    }
    return 'url(./assets/images/banner-placeholder.webp)'
  }

  public openBannerLink() {
    if (this.isBannerEnabled) {
      switch (this.languageService.current()) {
        case 'en': {
          return window.open('https://velocityiot.io/free-trial-telematic-insights/', '_blank')
        }
        case 'es': {
          return window.open(
            'https://m2mdataglobal.com/conectividad-m2m-global/?utm_source=telematicsinsights&utm_medium=cpc&utm_campaign=telematics_insights_conectividad/',
            '_blank',
          )
        }
      }
    }
  }
}
